import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  UserPlus, 
  CheckCircle2, 
  Trash2,
  AlertTriangle,
  Search,
  Filter,
  Download,
  X
} from 'lucide-react';
import { usersAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const ApprovalHistoryTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch approval history
  const { data: approvalHistory, isLoading, error } = useQuery({
    queryKey: ['approvalHistory', debouncedSearchTerm, filterAction, filterStatus],
    queryFn: () => usersAPI.getApprovalHistory({
      search: debouncedSearchTerm || undefined,
      actionType: filterAction === 'all' ? undefined : filterAction,
      status: filterStatus === 'all' ? undefined : filterStatus
    }),
    select: (data) => data?.data?.data?.history || data?.data?.history || []
  });



  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'create':
        return <UserPlus className="h-4 w-4 text-blue-600" />;
      case 'activate':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'deactivate':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'delete':
        return <Trash2 className="h-4 w-4 text-red-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'create':
        return 'Create User';
      case 'activate':
        return 'Activate User';
      case 'deactivate':
        return 'Deactivate User';
      case 'delete':
        return 'Delete User';
      default:
        return actionType;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending_l2':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending_l2':
        return 'Pending L2';
      default:
        return status;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      case 'pending_l2':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  };

  const getUserDisplayName = (user) => {
    // Handle userDisplayData format (from approval requests)
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.fullName) {
      return user.fullName;
    }
    if (user?.companyName) {
      return user.companyName;
    }
    
    // Handle personalInfo/corporateInfo format (from user objects)
    if (user?.personalInfo?.firstName && user?.personalInfo?.lastName) {
      return `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
    }
    if (user?.personalInfo?.fullName) {
      return user.personalInfo.fullName;
    }
    if (user?.corporateInfo?.companyName) {
      return user.corporateInfo.companyName;
    }
    
    return user?.email || 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    if (!approvalHistory || approvalHistory.length === 0) {
      return;
    }

    const headers = [
      'Action Type',
      'User Name',
      'User Email',
      'Status',
      'Initiated By',
      'Initiated At',
      'Approved/Rejected By',
      'Approved/Rejected At',
      'Comments',
      'Approval/Rejection Comments'
    ];

    const csvData = approvalHistory.map(record => [
      getActionLabel(record.actionType),
      getUserDisplayName(record.userDisplayData || record.userData),
      record.userDisplayData?.email || record.userData?.email || '',
      getStatusLabel(record.status),
      getUserDisplayName(record.initiatorId),
      formatDate(record.createdAt),
      record.status === 'approved' ? getUserDisplayName(record.approvedBy) : 
        record.status === 'rejected' ? getUserDisplayName(record.rejectedBy) : '',
      record.status === 'approved' ? formatDate(record.approvedAt) : 
        record.status === 'rejected' ? formatDate(record.rejectedAt) : '',
      record.comments || '',
      record.approvalComments || record.rejectionReason || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `approval_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">Failed to load approval history</p>
      </div>
    );
  }

  const filteredHistory = approvalHistory.filter(record => {
    if (filterAction !== 'all' && record.actionType !== filterAction) {
      return false;
    }
    if (filterStatus !== 'all' && record.status !== filterStatus) {
      return false;
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const userName = getUserDisplayName(record.userDisplayData || record.userData).toLowerCase();
      const userEmail = (record.userDisplayData?.email || record.userData?.email)?.toLowerCase() || '';
      const initiatorName = getUserDisplayName(record.initiatorId).toLowerCase();
      const approverName = record.approvedBy ? getUserDisplayName(record.approvedBy).toLowerCase() : '';
      const rejectorName = record.rejectedBy ? getUserDisplayName(record.rejectedBy).toLowerCase() : '';
      return userName.includes(searchLower) || 
             userEmail.includes(searchLower) || 
             initiatorName.includes(searchLower) ||
             approverName.includes(searchLower) ||
             rejectorName.includes(searchLower);
    }
    return true;
  });

  return (
    <div className="space-y-6">
    

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white border border-light-gray rounded-lg p-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search by user name, email, initiator, or approver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10"           />
        </div>
        <div className="sm:w-48">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="form-input"             >
            <option value="all">All Actions</option>
            <option value="create">Create User</option>
            <option value="activate">Activate User</option>
            <option value="deactivate">Deactivate User</option>
            <option value="delete">Delete User</option>
          </select>
        </div>
        <div className="sm:w-48">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="pending_l2">Pending L2</option>
          </select>
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredHistory.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No approval history found</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm || filterAction !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'No approval requests have been processed yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
              {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Approval History</h2>
         
        </div>
        <div className="flex items-center space-x-3 px-6">
          <div className="text-sm text-gray-500">
            {filteredHistory.length} record{filteredHistory.length !== 1 ? 's' : ''}
          </div>
          <button
            onClick={exportToCSV}
            disabled={!approvalHistory || approvalHistory.length === 0}
            className="btn-primary"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">
                    Action
                  </th>
                  <th className="table-header-cell">
                    User
                  </th>
                  <th className="table-header-cell">
                    Status
                  </th>
                  <th className="table-header-cell">
                    Initiated By
                  </th>
                  <th className="table-header-cell">
                    Initiated At
                  </th>
                  <th className="table-header-cell">
                    Approved/Rejected By
                  </th>
                  <th className="table-header-cell">
                    Action Date
                  </th>

                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHistory.map((record) => (
                  <tr key={record._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getActionIcon(record.actionType)}
                        <span className="text-sm font-medium text-gray-900">
                          {getActionLabel(record.actionType)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getUserDisplayName(record.userDisplayData || record.userData)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {record.userDisplayData?.email || record.userData?.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(record.status)}
                        <span className={`badge ${getStatusBadge(record.status)}`}>
                          {getStatusLabel(record.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getUserDisplayName(record.initiatorId)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {record.initiatorId?.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(record.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.status === 'approved' && record.approvedBy ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {getUserDisplayName(record.approvedBy)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {record.approvedBy?.email}
                          </div>
                        </div>
                      ) : record.status === 'rejected' && record.rejectedBy ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {getUserDisplayName(record.rejectedBy)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {record.rejectedBy?.email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.status === 'approved' && record.approvedAt ? 
                        formatDate(record.approvedAt) : 
                        record.status === 'rejected' && record.rejectedAt ? 
                          formatDate(record.rejectedAt) : 
                          '-'
                      }
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


    </div>
  );
};

export default ApprovalHistoryTab;
